import { Autocomplete } from 'components/utility/autocomplete'
import { useMulControlledInput } from 'components/utility/hooks/useMulControlledInput'
import { Validator } from 'components/utility/validator'
import { asMoney } from 'lib/utilities'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { InputField } from '../Requester/Components/inputField'
import { postCreateCreditCard, postPurchaseQuestionnaire, postSetupIntent } from '../api/requester'
import { capitalize } from 'lodash-es'

const USStates = Object.keys(Constants.US_States)
  .filter((key) => Constants.US_States[key].country === 'US')
  .map((key) => ({
    id: key,
    name: Constants.US_States[key]?.name,
  }))

const PAYMENT_FORM = {
  name: {
    name: 'name',
    label: 'name',
    placeholder: 'name',
    required: true,
    type: 'text',
  },
  billing_address: {
    name: 'address_line1',
    label: 'billing address',
    placeholder: 'address',
    required: true,
    type: 'text',
  },
  suite: {
    name: 'address_line2',
    label: 'floor/suite',
    placeholder: 'suite',
    required: false,
    type: 'text',
  },
  city: {
    name: 'address_city',
    label: 'city',
    placeholder: 'city',
    required: true,
    type: 'text',
  },
  state: {
    name: 'address_state',
    label: 'state',
    placeholder: 'Select state',
    required: true,
    type: 'text',
  },
  zip: {
    name: 'address_zip',
    label: 'zip',
    placeholder: 'XXXXX',
    required: true,
    type: 'text',
  },
}

const INITIAL_VALUES = {
  name: '',
  address_line1: '',
  address_line2: '',
  address_city: '',
  address_state: '',
  address_zip: '',
}

const SCHEMA = {
  name: new Validator().required(),
  address_line1: new Validator().required(),
  address_city: new Validator().required(),
  address_state: new Validator().required(),
  address_zip: new Validator().required(),
}

const SelectList = ({ value = '', error, id, name, label, required, placeholder, options, onBlur, onChange }) => {
  const internalId = useId()

  return (
    <div className="dome-d-flex dome-flex-column">
      <label htmlFor={id ?? internalId} style={{ margin: 0 }} className="dome-p3 dome-color-med-grey">
        {label}
        {required ? '*' : ''}
      </label>
      <div className="input-field-container dome-d-flex dome-align-center dome-gap12">
        <select
          name={name}
          id={id ?? internalId}
          style={{
            width: '100%',
            borderRadius: 6,
            padding: '8.5px 12px',
            border: '0.5px solid #898989',
          }}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        >
          <option disabled={true} value="">
            {placeholder}
          </option>
          {options?.map((state) => (
            <option key={state.id} value={state.id}>
              {state.name}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <div style={{ color: '#F64141', fontSize: '12px', marginTop: '2px' }}>
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

const getUserPaymentMethodsValueLabel = (method) => {
  return `${capitalize(method.type)} ...${method.last_4} (Expires ${method.expires[0]}/${method.expires[1]})`
}

export default function PaymentForm({
  hashedId,
  purchaserToken,
  publishableStripeKey,
  userPaymentMethods,
  priceTotalInCents,
  onBack,
  hasOnBack,
  onSubmit,
  onSuccess,
  onError,
}) {
  const cardRef = useRef()
  const stripeRef = useRef()
  const [isSaveCard, setIsSaveCard] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const { values, setValues, onChange, errors, onBlur, validateForm, isDirty, isValid } = useMulControlledInput({
    initialValue: INITIAL_VALUES,
    schema: SCHEMA,
  })

  useEffect(() => {
    if (selectedCard) return

    const stripe = Stripe(publishableStripeKey, { locale: 'en' })
    const elements = stripe.elements()
    const card = elements.create('card', { hidePostalCode: true })
    card.mount('.card-number')
    cardRef.current = card
    stripeRef.current = stripe
  }, [publishableStripeKey, selectedCard])

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (submitting) return
    onSubmit?.()
    setSubmitting(true)

    const errors = validateForm()
    const validForm = Object.keys(errors).length === 0

    if (!selectedCard && !validForm) {
      setSubmitting(false)
      return
    }

    try {
      let paymentMethodId
      let userPaymentMethodId
      let paymentToken

      if (selectedCard) {
        userPaymentMethodId = selectedCard.id
      } else if (isSaveCard) {
        paymentToken = await createStripeToken()
      } else {
        paymentMethodId = await createPaymentMethodDirectly()
      }

      await purchaseQuestionnaire({
        user_payment_method_id: userPaymentMethodId,
        payment_method_id: paymentMethodId,
        payment_token: paymentToken,
        save_payment_method: isSaveCard,
      })
      onSuccess?.()
    } catch (error) {
      console.log('error', error)
      onError?.(error)
    } finally {
      setSubmitting(false)
    }
  }

  const createStripeToken = async () => {
    const details = {
      name: values.name,
      address_city: values.address_city,
      address_country: 'US',
      address_line1: values.address_line1,
      address_line2: values.address_line2,
      address_state: values.address_state,
      address_zip: values.address_zip,
    }

    try {
      const result = await stripeRef.current.createToken(cardRef.current, details)
      if (result.error) {
        throw result.error?.message
      } else {
        return result.token.id
      }
    } catch (error) {
      throw error
    }
  }

  const createPaymentMethodDirectly = async () => {
    const { error, paymentMethod } = await stripeRef.current.createPaymentMethod({
      type: 'card',
      card: cardRef.current,
      billing_details: {
        name: values.name,
        address: {
          city: values.address_city,
          country: 'US',
          line1: values.address_line1,
          line2: values.address_line2,
          state: values.address_state,
          postal_code: values.address_zip,
        },
      },
    })
    if (error) throw error?.message
    return paymentMethod.id
  }

  const purchaseQuestionnaire = async ({
    user_payment_method_id,
    payment_method_id,
    save_payment_method,
    payment_token,
  }) => {
    try {
      const res = await postPurchaseQuestionnaire({
        hashedId,
        purchaserToken,
        user_payment_method_id,
        payment_method_id,
        save_payment_method,
        payment_token,
      })
      if (res.data?.paid && res.data?.paid_at) return res
      throw 'Your card has not been charged. Please try again.'
    } catch (error) {
      const status = error.response?.status
      if (status === 422 || status === 400) {
        throw error.response?.data?.error || 'Your card has not been charged. Please try again.'
      } else {
        throw 'Your card has not been charged. Please try again.'
      }
    }
  }

  const btnSubmitClassName = useMemo(() => {
    if (selectedCard) return 'dome-btn-dark-green'
    if (submitting) return 'dome-btn-disabled'
    if (!isValid || !isDirty) return 'dome-btn-disabled'
    return 'dome-btn-dark-green'
  }, [isValid, isDirty, submitting, selectedCard])

  const userPaymentMethodsOptions = useMemo(
    () =>
      userPaymentMethods.map((method) => ({
        ...method,
        keyLabel: getUserPaymentMethodsValueLabel(method),
      })),
    [userPaymentMethods]
  )

  return (
    <form
      method="post"
      acceptCharset="UTF-8"
      action="/users/create_credit_card"
      onSubmit={handleSubmit}
      className="dome-d-flex dome-full-width dome-flex-column dome-gap18"
    >
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span className="dome-h3 dome-color-dark-grey">add payment method</span>
        <span className="dome-p1 dome-text-w300 dome-color-darkest-grey">
          Property management will complete the questionnaire after receiving payment. You will receive an email with a
          link to the questionnaire once it’s complete.
        </span>
      </div>
      <div className="dome-d-flex dome-full-width dome-flex-column dome-gap18">
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <span className="dome-p1 dome-color-darkest-grey">Credit/Debit Payment</span>
          <Autocomplete
            labelKey="choose method*"
            placeholder="+ add new card"
            options={userPaymentMethodsOptions}
            getValueLabel={() => (selectedCard ? getUserPaymentMethodsValueLabel(selectedCard) : '+ add new card')}
            valueKey="keyLabel"
            getOptionLabel={(item) => (
              <div className="dome-d-flex dome-color-med-grey dome-justify-between">
                <span>
                  {capitalize(item.type)}: {item.last_4}
                </span>
                <span>*exp {`${item.expires[0]}/${item.expires[1]}`}</span>
              </div>
            )}
            onSelect={setSelectedCard}
            getTopOptionListItem={
              <div onClick={() => setSelectedCard(null)} className="dome-p1 dome-color-dark-grey ">
                + add new card
              </div>
            }
          />
        </div>
        {!selectedCard && (
          <div className="payment-method-form dome-d-flex dome-flex-column dome-gap6">
            <InputField
              {...PAYMENT_FORM.name}
              onBlur={onBlur}
              onChange={onChange}
              key={PAYMENT_FORM.name.name}
              value={values[PAYMENT_FORM.name.name]}
              error={errors[PAYMENT_FORM.name.name]}
            />
            <div className="auto-questionnaire-input-field">
              <label className="dome-p3 dome-color-med-grey">card number*</label>
              <div style={{ borderRadius: 6 }} className="form-control card-number"></div>
            </div>
            <div className="dome-d-flex dome-full-width dome-gap6">
              <div className="dome-flex-1">
                <InputField
                  {...PAYMENT_FORM.billing_address}
                  onBlur={onBlur}
                  onChange={onChange}
                  key={PAYMENT_FORM.billing_address.name}
                  value={values[PAYMENT_FORM.billing_address.name]}
                  error={errors[PAYMENT_FORM.billing_address.name]}
                />
              </div>
              <div style={{ width: 67 }}>
                <InputField
                  {...PAYMENT_FORM.suite}
                  onBlur={onBlur}
                  onChange={onChange}
                  key={PAYMENT_FORM.suite.name}
                  value={values[PAYMENT_FORM.suite.name]}
                  error={errors[PAYMENT_FORM.suite.name]}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
              <InputField
                {...PAYMENT_FORM.city}
                onBlur={onBlur}
                onChange={onChange}
                key={PAYMENT_FORM.city.name}
                value={values[PAYMENT_FORM.city.name]}
                error={errors[PAYMENT_FORM.city.name]}
              />
              <SelectList
                {...PAYMENT_FORM.state}
                value={values[PAYMENT_FORM.state.name]}
                error={errors[PAYMENT_FORM.state.name]}
                options={USStates}
                onBlur={onBlur}
                onChange={onChange}
              />
              <InputField
                {...PAYMENT_FORM.zip}
                onBlur={onBlur}
                onChange={onChange}
                key={PAYMENT_FORM.zip.name}
                value={values[PAYMENT_FORM.zip.name]}
                error={errors[PAYMENT_FORM.zip.name]}
              />
            </div>
          </div>
        )}
      </div>
      <div className="dome-d-flex dome-flex-column dome-gap12">
        {!selectedCard && (
          <div className="dome-d-flex dome-align-start dome-gap6">
            <input
              type="checkbox"
              value={isSaveCard}
              className="dome-cursor-pointer"
              onChange={(event) => setIsSaveCard(event.target.checked)}
              id="payment-method-confirm"
            />
            <label htmlFor="payment-method-confirm" className="dome-d-flex dome-flex-column dome-cursor-pointer">
              <span className="dome-p3 dome-color-med-grey">Save my credit card information</span>
              <span className="dome-p4 dome-color-med-grey dome-text-style-italic">
                For your convenience, we can securely save your credit card info for future purchases.{' '}
              </span>
            </label>
          </div>
        )}
        <div className="dome-d-flex dome-justify-between dome-align-center">
          <button
            type="button"
            onClick={onBack}
            style={{ visibility: hasOnBack ? 'initial' : 'hidden' }}
            className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-chevron-left"
          >
            back
          </button>
          <button
            type="submit"
            disabled={!selectedCard && (submitting || !isValid || !isDirty)}
            className={clsx('dome-btn dome-btn-base ', btnSubmitClassName)}
          >
            purchase
          </button>
        </div>
      </div>
    </form>
  )
}
